import type { AssetClass, DailyBar, MonitoringWindow } from "../types";

function scalePath(values: number[], width: number, height: number, pad = 22) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  return values
    .map((value, index) => {
      const x = pad + (index / Math.max(values.length - 1, 1)) * (width - pad * 2);
      const y = height - pad - ((value - min) / span) * (height - pad * 2);
      return `${index === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
}

function formatAxisValue(value: number, values: number[], assetClass: AssetClass) {
  const span = Math.max(...values) - Math.min(...values);
  if (assetClass === "rate") return `${value.toFixed(span < 0.1 ? 3 : 2)}%`;
  if (assetClass === "fx") {
    const digits = Math.abs(value) < 10 ? (span < 0.1 ? 4 : 3) : span < 5 ? 2 : 1;
    return value.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  }
  const absolute = Math.abs(value);
  if (absolute >= 10_000) {
    return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value);
  }
  if (absolute >= 1_000) return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
  const digits = span < 1 ? 2 : absolute < 100 ? 1 : 0;
  return value.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function formatAxisDate(date: string, range: MonitoringWindow) {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date.slice(0, 7);
  if (range === "3Y" || range === "5Y" || range === "10Y") return String(parsed.getUTCFullYear());
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    ...(range === "1Y" ? { year: "2-digit" as const } : {}),
    timeZone: "UTC"
  }).format(parsed);
}

export function Sparkline({ history, danger, assetClass, range }: {
  history: DailyBar[];
  danger?: boolean;
  assetClass: AssetClass;
  range: MonitoringWindow;
}) {
  const width = 250;
  const height = 88;
  const plot = { left: 4, right: 208, top: 5, bottom: 69 };
  const validHistory = history.filter((bar) => Number.isFinite(bar.close));
  const values = validHistory.map((bar) => bar.close);
  if (!values.length) {
    return <div className="sparkline sparkline-empty" aria-label="No stored history" />;
  }
  const low = Math.min(...values);
  const high = Math.max(...values);
  const mid = (high + low) / 2;
  const span = high - low || Math.max(Math.abs(high) * 0.02, 1);
  const paddedLow = low - span * 0.04;
  const paddedHigh = high + span * 0.04;
  const xAt = (index: number) => plot.left + (index / Math.max(values.length - 1, 1)) * (plot.right - plot.left);
  const yAt = (value: number) => plot.bottom - ((value - paddedLow) / (paddedHigh - paddedLow)) * (plot.bottom - plot.top);
  const path = values.map((value, index) => `${index === 0 ? "M" : "L"}${xAt(index).toFixed(1)} ${yAt(value).toFixed(1)}`).join(" ");
  const midpointIndex = Math.floor((validHistory.length - 1) / 2);
  const dates: [string, string, string] = [
    validHistory[0]!.date,
    validHistory[midpointIndex]!.date,
    validHistory[validHistory.length - 1]!.date
  ];
  const levels = [high, mid, low];
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="sparkline quiet-grid-chart"
      role="img"
      aria-label={`${range} history from ${dates[0]} to ${dates[2]}, high ${formatAxisValue(high, values, assetClass)}, low ${formatAxisValue(low, values, assetClass)}`}
    >
      {levels.map((level, index) => {
        const y = plot.top + index * ((plot.bottom - plot.top) / 2);
        return <g key={`${level}-${index}`}>
          <path className="sparkline-grid" d={`M${plot.left} ${y}H${plot.right}`} />
          <text className="sparkline-axis-label" x={plot.right + 4} y={y + 2.5}>{formatAxisValue(level, values, assetClass)}</text>
        </g>;
      })}
      <path className="sparkline-series" d={path} stroke={danger ? "#ff5d6e" : "#42a5ff"} />
      {dates.map((date, index) => (
        <text
          key={`${date}-${index}`}
          className={`sparkline-axis-label sparkline-axis-x ${index === 1 ? "sparkline-axis-mid" : ""}`}
          x={[plot.left, (plot.left + plot.right) / 2, plot.right][index]}
          y={84}
          textAnchor={index === 0 ? "start" : index === 2 ? "end" : "middle"}
        >
          {formatAxisDate(date, range)}
        </text>
      ))}
    </svg>
  );
}

export function DetailHistoryChart({ history }: { history: DailyBar[] }) {
  const width = 760;
  const height = 240;
  const values = history.map((bar) => bar.close);
  const high = Math.max(...values);
  const low = Math.min(...values);
  const min = low * 0.96;
  const max = high * 1.04;
  const toY = (value: number) => height - 28 - ((value - min) / (max - min)) * (height - 54);
  const highY = toY(high);
  const lowY = toY(low);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Ten year history with high and low markers">
      <rect width={width} height={height} fill="#07101a" />
      <g stroke="rgba(116,142,172,.16)">
        {[32, 76, 120, 164, 208].map((y) => <path key={y} d={`M42 ${y}H720`} />)}
        {[94, 214, 334, 454, 574, 694].map((x) => <path key={x} d={`M${x} 24V214`} />)}
      </g>
      <path d={scalePath(values, width, height, 42)} fill="none" stroke="#42a5ff" strokeWidth="2.4" />
      <path d={`M42 ${highY}H720`} stroke="#24d18f" strokeDasharray="5 5" />
      <path d={`M42 ${lowY}H720`} stroke="#ff5d6e" strokeDasharray="5 5" />
      <text x="48" y={highY - 6} fill="#24d18f" fontSize="11">10Y high</text>
      <text x="48" y={lowY - 6} fill="#ff5d6e" fontSize="11">10Y low</text>
    </svg>
  );
}
